/**
 * ============================================================================
 *  File        : frontend/src/lib/permissions.ts
 *  Module      : lib
 *  Purpose     : RBAC permission keys（對齊 spec §9.2）與 all-access 角色判定
 *  Author      : EF Engineering
 *  Created     : 2026-05-31
 *  Updated     : 2026-05-31
 *  License     : Proprietary - ECO FAITH Platform v3.0
 * ============================================================================
 */

/** 具備全平台存取權的角色（spec §9.1 super_admin；platform 慣例 platform_admin）。 */
export const ALL_ACCESS_ROLES: readonly string[] = ['super_admin', 'platform_admin']

/**
 * spec §8.2 各頁面所需的權限鍵。
 * 採模組級語意（與後端 permission 命名空間對齊）；無對應後端 permission 的
 * 通用查詢頁（dashboard / cases 列表）給 null，代表登入即可見。
 */
export const PERMISSIONS = {
  caseRead: 'marketplace.cases.view',
  caseCreate: 'marketplace.cases.create',
  caseManage: 'marketplace.cases.update',
  // 設定会場（PUT /cases/:id/venue）後端 gate = marketplace.cases.update（v1.rs listing_routes）。
  venueSet: 'marketplace.cases.update',
  shipmentManage: 'marketplace.shipment_documents.upload',
  // 出荷確定（POST /cases/:id/ship）後端 gate = logistics.shipments.manage（v1.rs shipment_routes）。
  shipConfirm: 'logistics.shipments.manage',
  receivingManage: 'operations.weighings.record',
  weightVarianceView: 'marketplace.weight_variance.view',
  // 重量差異 review 建立 / happy-path 推進（POST /cases/:id/weight-audits）= marketplace.weight_variance.create。
  weightVarianceCreate: 'marketplace.weight_variance.create',
  weightManualCorrect: 'marketplace.weight_variance.resolve',
  // 報連相（hourensou，spec §10 / §D5）：list = view，resolve = resolve。
  hourensouView: 'marketplace.hourensou.view',
  hourensouResolve: 'marketplace.hourensou.resolve',
  settlementP1: 'marketplace.settlements.manual_amount.edit',
  auctionManage: 'marketplace.bid_collab.round.manage',
  auctionRoundManage: 'marketplace.bid_collab.round.manage',
  auctionBidderManage: 'marketplace.bid_collab.bidder.manage',
  auctionPreviewManage: 'marketplace.bid_collab.preview.manage',
  auctionPreviewBook: 'marketplace.bid_collab.preview.book',
  auctionBidSubmit: 'marketplace.bid_collab.bid.submit',
  auctionBidView: 'marketplace.bid_collab.bid.view',
  auctionAwardConfirm: 'marketplace.bid_collab.award.confirm',
  auctionAwardView: 'marketplace.bid_collab.award.view',
  adjustmentPriceAdjust: 'marketplace.member_decisions.create',
  shareManage: 'marketplace.settlements.calculate',
  settlementP2: 'marketplace.settlements.manual_amount.edit',
  // 二回目 発行（POST /cases/:id/settlements/p2/runs/:run_id/issue）= marketplace.settlements.issue。
  settlementIssue: 'marketplace.settlements.issue',
  // 二回目 入金確認（POST /cases/:id/settlements/p2/payment-confirmation）= marketplace.settlements.payment_confirm。
  settlementPaymentConfirm: 'marketplace.settlements.payment_confirm',
  finalWeightManage: 'operations.weighings.record',
  settlementP3: 'marketplace.settlements.calculate',
  // 結案（review / close spine endpoints）後端 gate = marketplace.cases.update（v1.rs 已驗）。
  caseClose: 'marketplace.cases.update',
  // 最終報告 read model（GET /cases/:id/final-report）= marketplace.final_report.view（formal spec §K）。
  finalReportView: 'marketplace.final_report.view',
  // 三方確認（POST /cases/:id/final-report/confirm）= marketplace.final_report.confirm。
  finalReportConfirm: 'marketplace.final_report.confirm',
  // Freee SLA（計時 + 提醒事件，不串接 Freee API）+ financial_documents（spec §10.3 / §B4）。
  freeeSlaView: 'marketplace.freee_sla.view',
  freeeSlaComplete: 'marketplace.freee_sla.complete',
  financialDocumentView: 'marketplace.financial_documents.view',
  financialDocumentCreate: 'marketplace.financial_documents.create',
  adminUserManage: 'iam.users.list',
  adminRoleManage: 'iam.roles.manage',
  masterPriceManage: 'system.configs.manage',
  reportRead: 'marketplace.settlements.view',
  auditRead: 'system.audit.view',
} as const

export type PermissionKey = (typeof PERMISSIONS)[keyof typeof PERMISSIONS]

/**
 * 最終報告 三方確認の persona → 該当 party を担える role。
 * 確認ボタンの persona ゲートに使用（actor が当該 party の代表 role を持つときのみ enable）。
 * EF orchestrator（ef_admin / all-access）は ef party を担う。
 */
export const FINAL_CONFIRM_PERSONA_ROLES: Record<'ef' | 'member' | 'venue', readonly string[]> = {
  ef: ['ef_admin', 'org_owner', ...ALL_ACCESS_ROLES],
  member: ['ef_member_rep'],
  venue: ['ef_venue_rep'],
} as const
